import axios from "axios";
import { BaseUrl } from "./Base";

const axiosInstance = axios.create({
  baseURL: BaseUrl,
  withCredentials: true,
});

/**
 * Upsert Special Promotion Ontario lead (Status = Lead).
 * Uses sendBeacon on page unload when possible so the request is not cancelled.
 */
export const SaveSpecialPromotionLead =
  (payload, options = {}) =>
  async (dispatch) => {
    const body = {
      fullName: payload.fullName || "",
      email: payload.email || "",
      phone: payload.phone || "",
      userId: payload.userId || null,
      instituteCourseId: payload.instituteCourseId || null,
      licenseType: payload.licenseType || null,
      bdePackageName: payload.bdePackageName || null,
      bdePackagePrice: payload.bdePackagePrice ?? null,
      driveTestPrice: payload.driveTestPrice ?? null,
      driveTestOptionName: payload.driveTestOptionName || null,
      inVehicleTrainingPrice: payload.inVehicleTrainingPrice ?? null,
      inVehicleOptionName: payload.inVehicleOptionName || null,
      inVehicleCustomHours: payload.inVehicleCustomHours ?? null,
      totalAmount: payload.totalAmount ?? null,
      notes: payload.notes || null,
      guardianContact: payload.guardianContact || null,
      preferredStartDate: payload.preferredStartDate || payload.startDate || null,
      // Honeypot — leave empty for real users
      website: payload.website || "",
    };

    if (
      !body.email?.trim() &&
      !String(body.phone || "")
        .replace(/\D/g, "")
        .match(/\d{7,}/)
    ) {
      return { skipped: true };
    }

    // Client-side spam short-circuit (backend also enforces this)
    if (String(body.website || "").trim()) {
      return { skipped: true, spam: true };
    }

    dispatch({ type: "SaveSpecialPromotionLeadRequest" });

    try {
      if (
        options.useBeacon &&
        typeof navigator !== "undefined" &&
        navigator.sendBeacon
      ) {
        const url = `${BaseUrl || ""}/api/special-promotion-lead`;
        const blob = new Blob([JSON.stringify(body)], {
          type: "application/json",
        });
        const queued = navigator.sendBeacon(url, blob);
        dispatch({
          type: "SaveSpecialPromotionLeadSuccess",
          payload: { beacon: true, queued },
        });
        return { beacon: true, queued };
      }

      const { data } = await axiosInstance.post(
        "/api/special-promotion-lead",
        body
      );
      dispatch({
        type: "SaveSpecialPromotionLeadSuccess",
        payload: data,
      });
      return data;
    } catch (error) {
      console.error("SaveSpecialPromotionLead error:", error);
      dispatch({
        type: "SaveSpecialPromotionLeadFailure",
        payload: error?.response?.data || error.message,
      });
      return null;
    }
  };

/** Create PayPal order for special promotion checkout (amount only). */
export const CreateSpecialPromotionPayPalOrder =
  (totalPayment, currency = "USD") =>
  async (dispatch) => {
    dispatch({ type: "CreateSpecialPromotionPayPalOrderRequest" });
    try {
      const { data } = await axiosInstance.post(
        "/api/special-promotion-paypal-order",
        { TotalPrice: totalPayment, Currency: currency }
      );

      if (!data?.id) {
        throw new Error(
          data?.error || data?.message || "PayPal did not return an order id"
        );
      }

      dispatch({
        type: "CreateSpecialPromotionPayPalOrderSuccess",
        payload: data,
      });
      // Always return a plain id string for PayPal Buttons createOrder
      return data;
    } catch (error) {
      dispatch({
        type: "CreateSpecialPromotionPayPalOrderFailure",
        payload: error?.response?.data || error.message,
      });
      throw error;
    }
  };

/**
 * Complete checkout after PayPal / card / Interac.
 * Expects a plain object; builds multipart FormData (files optional).
 */
export const CompleteSpecialPromotionCheckout =
  (payload) => async (dispatch) => {
    dispatch({ type: "CompleteSpecialPromotionCheckoutRequest" });

    try {
      const formData = new FormData();
      const append = (key, value) => {
        if (value === undefined || value === null || value === "") return;
        formData.append(key, value);
      };

      append("paymentMethod", payload.paymentMethod);
      append("paypalOrderId", payload.paypalOrderId);
      append("fullName", payload.fullName);
      append("email", payload.email);
      append("phone", payload.phone);
      append("address", payload.address);
      append("notes", payload.notes);
      append("guardianContact", payload.guardianContact);
      append("licenseType", payload.licenseType);
      append("instituteCourseId", payload.instituteCourseId);
      append("bdePackageName", payload.bdePackageName);
      append("bdePackagePrice", payload.bdePackagePrice);
      append("driveTestPrice", payload.driveTestPrice);
      append("driveTestOptionName", payload.driveTestOptionName);
      append("inVehicleTrainingPrice", payload.inVehicleTrainingPrice);
      append("inVehicleOptionName", payload.inVehicleOptionName);
      append("inVehicleCustomHours", payload.inVehicleCustomHours);
      append("totalAmount", payload.totalAmount);
      append(
        "preferredStartDate",
        payload.preferredStartDate || payload.startDate
      );

      if (payload.paymentMethod === "creditcard" && payload.cardDetails) {
        append("CardBrand", payload.cardDetails.brand);
        append("CardExpMonth", payload.cardDetails.exp_month);
        append("CardExpYear", payload.cardDetails.exp_year);
        append("CardLast4", payload.cardDetails.last4);
        append("CardPostalCode", payload.cardDetails.postal_code);
      }

      if (
        payload.paymentMethod === "interacETransfer" &&
        payload.interacDetails
      ) {
        append("interacfirstName", payload.interacDetails.firstName);
        append("interaclastName", payload.interacDetails.lastName);
        append("interacsenderEmail", payload.interacDetails.senderEmail);
        append("interacconfirmationId", payload.interacDetails.confirmationId);
        append("interacpaymentDate", payload.interacDetails.paymentDate);
        if (payload.interacDetails.interacscreenshot) {
          formData.append(
            "interacscreenshot",
            payload.interacDetails.interacscreenshot
          );
        }
      }

      if (payload.licenseFront) {
        formData.append("LatestLicense", payload.licenseFront);
      }
      if (payload.licenseBack) {
        formData.append("LatestLicenseBack", payload.licenseBack);
      }

      console.log("💳 CompleteSpecialPromotionCheckout:", {
        paymentMethod: payload.paymentMethod,
        email: payload.email,
        totalAmount: payload.totalAmount,
      });

      const { data } = await axiosInstance.post(
        "/api/special-promotion-checkout",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      dispatch({
        type: "CompleteSpecialPromotionCheckoutSuccess",
        payload: data,
      });
      return data;
    } catch (error) {
      console.error("CompleteSpecialPromotionCheckout error:", error);
      dispatch({
        type: "CompleteSpecialPromotionCheckoutFailure",
        payload: error?.response?.data || error.message,
      });
      throw error;
    }
  };

/** Admin: fetch full detail for a single Pending Activation learner */
export const FetchPendingActivationDetailA =
  ({ specialPromotionId, userId }) =>
  async (dispatch) => {
    try {
      dispatch({ type: "FetchPendingActivationDetailRequest" });

      const { data } = await axiosInstance.post("/api/FetchPendingActivationDetail", {
        specialPromotionId,
        userId,
      });

      dispatch({ type: "FetchPendingActivationDetailSuccess", payload: data });
      return data;
    } catch (error) {
      const message = error?.response?.data?.message || "Something went wrong.";
      dispatch({ type: "FetchPendingActivationDetailFailure", payload: message });
      return null;
    }
  };

/**
 * Admin Pending Activation: create CoursePackage for selected institute course
 * using Special Promotion paid amount + calculated hours.
 */
export const CreatePendingActivationPackageA =
  ({ specialPromotionId, instituteCourseId }) =>
  async (dispatch) => {
    try {
      dispatch({ type: "CreatePendingActivationPackageRequest" });

      const { data } = await axiosInstance.post(
        "/api/create-pending-activation-package",
        { specialPromotionId, instituteCourseId }
      );

      dispatch({
        type: "CreatePendingActivationPackageSuccess",
        payload: data,
      });
      return { success: true, data };
    } catch (error) {
      console.error("CreatePendingActivationPackage error:", error);
      dispatch({
        type: "CreatePendingActivationPackageFailure",
        payload: error?.response?.data || error.message,
      });
      return {
        success: false,
        error: error?.response?.data || error,
      };
    }
  };

/**
 * Admin Pending Activation: save Other institute details (non–VLI-contract path)
 * + learner profile (User / StudentInfo). No course enrollment.
 * Sends FormData so new profile/license images upload via multer (never base64).
 */
export const SubmitPendingActivationOtherInstituteA =
  (payload) => async (dispatch) => {
    try {
      dispatch({ type: "SubmitPendingActivationOtherInstituteRequest" });

      const formData = new FormData();
      formData.append(
        "specialPromotionId",
        payload.specialPromotionId || ""
      );
      if (payload.userId) formData.append("userId", payload.userId);
      formData.append(
        "preAddedInstituteName",
        payload.preAddedInstituteName || ""
      );
      formData.append("otherInstituteName", payload.otherInstituteName || "");
      formData.append("instituteCity", payload.instituteCity || "");
      formData.append(
        "otherInstituteContactPerson",
        payload.otherInstituteContactPerson || ""
      );
      formData.append(
        "otherInstituteContactNumber",
        payload.otherInstituteContactNumber || ""
      );
      formData.append(
        "studentData",
        JSON.stringify(payload.studentData || {})
      );

      // Existing stored paths only (never blob:/data: URLs)
      if (
        payload.studentImage &&
        typeof payload.studentImage === "string" &&
        !payload.studentImage.startsWith("blob:") &&
        !payload.studentImage.startsWith("data:") &&
        payload.studentImage.length <= 255
      ) {
        formData.append("studentImage", payload.studentImage);
      }
      if (
        payload.latestLicensePath &&
        typeof payload.latestLicensePath === "string" &&
        payload.latestLicensePath.length <= 255
      ) {
        formData.append("latestLicense", payload.latestLicensePath);
      }
      if (
        payload.latestLicenseBackPath &&
        typeof payload.latestLicenseBackPath === "string" &&
        payload.latestLicenseBackPath.length <= 255
      ) {
        formData.append("latestLicenseBack", payload.latestLicenseBackPath);
      }

      // New file uploads
      if (payload.imageFile instanceof File) {
        formData.append("StudentImage", payload.imageFile);
      }
      if (payload.latestLicenseFile instanceof File) {
        formData.append("LatestLicense", payload.latestLicenseFile);
      }
      if (payload.latestLicenseBackFile instanceof File) {
        formData.append("LatestLicenseBack", payload.latestLicenseBackFile);
      }

      const { data } = await axiosInstance.post(
        "/api/submit-pending-activation-other-institute",
        formData
      );

      dispatch({
        type: "SubmitPendingActivationOtherInstituteSuccess",
        payload: data,
      });
      return { success: true, data };
    } catch (error) {
      console.error("SubmitPendingActivationOtherInstitute error:", error);
      dispatch({
        type: "SubmitPendingActivationOtherInstituteFailure",
        payload: error?.response?.data || error.message,
      });
      return {
        success: false,
        error: error?.response?.data || error,
      };
    }
  };

/** Other institutes (name + contact/city) for Pre Added Institute dropdown */
export const FetchSpecialPromotionOtherInstituteNamesA =
  () => async (dispatch) => {
    try {
      dispatch({ type: "FetchSpecialPromotionOtherInstituteNamesRequest" });

      const { data } = await axiosInstance.get(
        "/api/pending-activation-other-institute-names"
      );

      dispatch({
        type: "FetchSpecialPromotionOtherInstituteNamesSuccess",
        payload: data,
      });

      const list = Array.isArray(data?.data) ? data.data : [];
      // Normalize: support legacy string[] or new object[]
      return list
        .map((item) => {
          if (typeof item === "string") {
            const name = String(item || "").trim();
            return name
              ? { name, contactPerson: "", contactNumber: "", instituteCity: "" }
              : null;
          }
          const name = String(item?.name || "").trim();
          if (!name) return null;
          return {
            name,
            contactPerson: String(item?.contactPerson || "").trim(),
            contactNumber: String(item?.contactNumber || "").trim(),
            instituteCity: String(item?.instituteCity || "").trim(),
          };
        })
        .filter(Boolean);
    } catch (error) {
      console.error("FetchSpecialPromotionOtherInstituteNames error:", error);
      dispatch({
        type: "FetchSpecialPromotionOtherInstituteNamesFailure",
        payload: error?.response?.data || error.message,
      });
      return [];
    }
  };

/**
 * Admin Pending Activation: save allocation + automatic schedule and enroll learner.
 * Payment was already collected on SpecialPromotionInOntario.
 */
export const CompletePendingActivationEnrollmentA =
  (enrollmentPayload) => async (dispatch) => {
    try {
      dispatch({ type: "CompletePendingActivationEnrollmentRequest" });

      const { data } = await axiosInstance.post(
        "/api/complete-pending-activation-enrollment",
        enrollmentPayload
      );

      dispatch({
        type: "CompletePendingActivationEnrollmentSuccess",
        payload: data,
      });
      return { success: true, data };
    } catch (error) {
      console.error("CompletePendingActivationEnrollment error:", error);
      dispatch({
        type: "CompletePendingActivationEnrollmentFailure",
        payload: error?.response?.data || error.message,
      });
      return {
        success: false,
        error: error?.response?.data || error,
      };
    }
  };

/** Admin: fetch SpecialPromotionInOntario records with Status = Paid */
export const FetchPaidSpecialPromotionsA = () => async (dispatch) => {
  try {
    dispatch({ type: "FetchPaidSpecialPromotionsRequest" });

    const { data } = await axiosInstance.post("/api/FetchPaidSpecialPromotions");

    dispatch({
      type: "FetchPaidSpecialPromotionsSuccess",
      payload: data,
    });

    return data;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      "Something went wrong. Please try again.";

    dispatch({
      type: "FetchPaidSpecialPromotionsFailure",
      payload: message,
    });

    return null;
  }
};

/** Admin: fetch SpecialPromotionInOntario where IsOtherInstitute = true */
export const FetchOtherInstituteSpecialPromotionsA = () => async (dispatch) => {
  try {
    dispatch({ type: "FetchOtherInstituteSpecialPromotionsRequest" });

    const { data } = await axiosInstance.post(
      "/api/FetchOtherInstituteSpecialPromotions"
    );

    dispatch({
      type: "FetchOtherInstituteSpecialPromotionsSuccess",
      payload: data,
    });

    return data;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      "Something went wrong. Please try again.";

    dispatch({
      type: "FetchOtherInstituteSpecialPromotionsFailure",
      payload: message,
    });

    return null;
  }
};

/** Admin: update Status for Other-institute special promotion */
export const UpdateOtherInstituteSpecialPromotionStatusA =
  ({ specialPromotionId, status, completedDate }) =>
  async (dispatch) => {
    try {
      dispatch({ type: "UpdateOtherInstituteSpecialPromotionStatusRequest" });

      const { data } = await axiosInstance.post(
        "/api/UpdateOtherInstituteSpecialPromotionStatus",
        { specialPromotionId, status, completedDate }
      );

      dispatch({
        type: "UpdateOtherInstituteSpecialPromotionStatusSuccess",
        payload: data,
      });

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      dispatch({
        type: "UpdateOtherInstituteSpecialPromotionStatusFailure",
        payload: message,
      });

      return { success: false, error: error?.response?.data || { message } };
    }
  };
